import { ApiModule, NetworkHelper } from '../module/index';
import {
  Flight,
  FlightsWidgets,
  SkyscannerResultsV3,
  SkyscannerSuggestResponse,
  IndicativeSearchRequest,
  FlightCore,
  MlAbstractResponse,
} from 'types';
import {
  map,
  mergeMap,
  repeatWhen,
  delay,
  toArray,
  reduce,
  tap,
} from 'rxjs/operators';
import { from, of, Observable } from 'rxjs';
import { parsePrice, mapResults } from '../../mapper/flights/searchMapperV3';
import {
  removeDuplicates,
  AgentsManager,
  agentTypeCondition,
} from '../../use-case';
import cloneDeep from 'lodash/cloneDeep';

export namespace FlightService {
  export const initSessionv3 = (criteria: Flight.SearchCriteria) => {
    const requestBody = criteria.toBody(Flight.RequestTypes.Search);
    const locale = NetworkHelper.getLocale();
    const country = NetworkHelper.getCountry();

    if (agentTypeCondition() === SkyscannerResultsV3.AgentType.Airline) {
      requestBody.query.excludedAgentsIds = AgentsManager.get().getAgentsIds();
    }

    requestBody.query.market = country;
    requestBody.query.locale = locale;
    requestBody.query.currency = NetworkHelper.getCurrency().currency;

    return ApiModule.get()
      .apiML.post<MlAbstractResponse<SkyscannerResultsV3.LivePricingResponse>>(
        '/flights/poll',
        requestBody,
      )
      .pipe(
        map(it => it.data),
        livePricingResponseFlows,
      );
  };

  const livePricingResponseFlows = (
    x: Observable<SkyscannerResultsV3.LivePricingResponse>,
  ) => {
    return x.pipe(
      tap(it => {
        if (it.status === SkyscannerResultsV3.ResultStatus.Failed) {
          throw new Error();
        }
      }),
      map(it => {
        composeAgentsIds(it);
        return mapResults(it);
      }),
      mergeMap(it => removeDuplicates(it)),
    );
  };

  export const autosuggest = (query: String) => {
    const locale = NetworkHelper.getLocale();
    const country = NetworkHelper.getCountry();

    const body = {
      query: {
        searchTerm: query,
        market: country,
        locale: locale,
      },
    };
    return ApiModule.get()
      .apiML.post<MlAbstractResponse<SkyscannerSuggestResponse>>(
        '/flights/suggest',
        body,
      )
      .pipe(map(it => it.data.places));
  };

  const composeAgentsIds = async (
    results: SkyscannerResultsV3.LivePricingResponse,
  ) => {
    try {
      const agentsIds = Object.keys(results.content.results.agents).filter(
        it =>
          results.content.results.agents[it].type ===
          SkyscannerResultsV3.AgentType.TravelAgent,
      );
      AgentsManager.get().pushAgentsIds(agentsIds);
    } catch {}
  };

  export const getLivePriceSessionV3 = (path: string) => {
    return of([1])
      .pipe(
        mergeMap(it =>
          ApiModule.get().apiML.get<
            MlAbstractResponse<SkyscannerResultsV3.LivePricingResponse>
          >(`/flights/poll?session=${path}`, NetworkHelper.getUTMParams()),
        ),
        map(it => it.data),
        livePricingResponseFlows,
      )
      .pipe(repeatWhen(notif => notif.pipe(delay(300))));
  };

  export const browseRoutes = (
    body: IndicativeSearchRequest,
  ): Observable<SkyscannerResultsV3.IndicativeSearchResponse> => {
    const locale = NetworkHelper.getLocale();
    const country = NetworkHelper.getCountry();

    body.query.market = country;
    body.query.locale = locale;
    body.query.currency = NetworkHelper.getCurrency().currency;

    return ApiModule.get()
      .apiML.post<
        MlAbstractResponse<SkyscannerResultsV3.IndicativeSearchResponse>
      >('/flights/search', body)
      .pipe(map(it => it.data));
  };

  export const checkPricesForHistory = (
    records: Flight.HistoryRecord[],
  ): Observable<Flight.HistoryRecord[]> => {
    return from(records).pipe(
      mergeMap(record =>
        browseRoutes(
          Flight.SearchCriteria.fromPersistance(record).toBody(
            Flight.RequestTypes.BrowsePrice,
          ),
        ).pipe(
          map(it => {
            const clone = { ...record };
            let price = 'n/a';
            try {
              price = getMinPrice(it).formatCurrency({
                isCent: false,
                round: true,
              });
            } catch (e) {}
            clone.price = price;
            clone.priceUpdateAt = new Date().getTime();
            return clone;
          }),
        ),
      ),
      toArray(),
    );
  };

  export const getMinPrice = (
    x: SkyscannerResultsV3.IndicativeSearchResponse,
  ) => {
    const quotesIds = ([] as string[])
      .concat(
        ...x.content.groupingOptions.byRoute.quotesGroups.map(
          it => it.quoteIds,
        ),
      )
      .distinct();
    const quote = quotesIds
      .map(id => x.content.results.quotes[id])
      .min(it => parsePrice(it.minPrice.amount, it.minPrice.unit));
    return parsePrice(quote.minPrice.amount, quote.minPrice.unit);
  };

  export const getAvailablePlaces = (
    place: SkyscannerResultsV3.PlaceShort,
  ): Observable<any> => {
    const queryLegs = [];
    queryLegs.push({
      originPlace: {
        queryPlace: {
          iata: place.iata,
        },
      },
      destinationPlace: {
        anywhereByCity: true,
      },
      anytime: true,
    });
    const requestBody: IndicativeSearchRequest = {
      query: {
        queryLegs,
      },
    };

    return browseRoutes(requestBody);
  };

  export const oneShotNearbyDates = (
    criteria: Flight.SearchCriteria,
    minPrice: number,
  ): Observable<FlightsWidgets.DateCell[]> => {
    const dates = FlightsWidgets.NearbyDates.build(criteria).dates;
    const splitter = dates.groupBy(it => it.departureDate.getMonth());

    const passengers = criteria.adults + criteria.childrenAges.length;

    const variations: {
      body: IndicativeSearchRequest;
      direction: FlightCore.Directionality;
    }[] = [];

    splitter.forEach(date => {
      const body = criteria
        .fromNearbyDatesWidget(date[0])
        .toBody<IndicativeSearchRequest>(Flight.RequestTypes.BrowsePrice);
      if (body.query.queryLegs.length > 1) {
        const bodyOut = cloneDeep(body);
        const bodyIn = cloneDeep(body);
        const outLeg = body.query.queryLegs[0];
        const inLeg = body.query.queryLegs[1];

        bodyOut.query.queryLegs = [];
        bodyOut.query.queryLegs.push(outLeg);
        bodyIn.query.queryLegs = [];
        bodyIn.query.queryLegs.push(inLeg);
        variations.push(
          {
            direction: FlightCore.Directionality.Inbound,
            body: bodyIn,
          },
          {
            direction: FlightCore.Directionality.Outbound,
            body: bodyOut,
          },
        );
      } else {
        variations.push({
          direction: FlightCore.Directionality.Outbound,
          body,
        });
      }
    });

    return from(variations).pipe(
      mergeMap(variation =>
        browseRoutes(variation.body).pipe(
          map(it => {
            return it.content.groupingOptions.byDate.quotesOutboundGroups.map(
              a => {
                return {
                  date: a.monthYearDate,
                  quotes: a.quoteIds.map(x => it.content.results.quotes[x]),
                  direction: variation.direction,
                } as SkyscannerResultsV3.DateWithQuotes;
              },
            );
          }),
        ),
      ),
      toArray(),
      reduce((acc, curr) => {
        const singleD = ([] as SkyscannerResultsV3.DateWithQuotes[]).concat(
          ...curr,
        );
        return acc.concat(...singleD);
      }, [] as SkyscannerResultsV3.DateWithQuotes[]),
      mergeMap(quotes => {
        return from(dates).pipe(
          map(x => {
            const dateToString = (date: Date) => {
              return `${date.getDate()}/${
                date.getMonth() + 1
              }/${date.getFullYear()}`;
            };

            const dateModelToString = (
              date: SkyscannerResultsV3.DateTypeShort,
            ) => {
              return `${date.day}/${date.month}/${date.year}`;
            };
            const departureDate = dateToString(x.departureDate);

            const tmp = { ...x };

            const departureQuotes = quotes.find(
              it =>
                dateModelToString(it.date) === departureDate &&
                it.direction === FlightCore.Directionality.Outbound,
            );

            if (departureQuotes) {
              const quote = departureQuotes.quotes.min(
                it => +it.minPrice.amount,
              );
              tmp.price = parsePrice(
                quote.minPrice.amount,
                quote.minPrice.unit,
              );

              if (x.returnDate) {
                const returnDate = x.returnDate
                  ? dateToString(x.returnDate)
                  : undefined;
                const returnQuotes = quotes.find(
                  it =>
                    dateModelToString(it.date) === returnDate &&
                    it.direction === FlightCore.Directionality.Inbound,
                );

                if (returnQuotes) {
                  const inBound = returnQuotes.quotes.min(
                    it => +it.minPrice.amount,
                  );
                  const price = parsePrice(
                    inBound.minPrice.amount,
                    inBound.minPrice.unit,
                  );
                  tmp.price = tmp.price ? tmp.price + price : price;
                } else {
                  tmp.price = undefined;
                }
              }
            }
            return tmp;
          }),
        );
      }),
      toArray(),
      map(it => {
        if (it.length === 0) {
          return it;
        }

        const entry =
          passengers === 1
            ? it.map(a => {
                if (!a.isSelected) {
                  return a;
                }

                return { ...a, price: minPrice };
              })
            : it;

        const sorted = entry.sort(
          (a, b) => a.departureDate.getTime() - b.departureDate.getTime(),
        );

        const cheapest = sorted
          .filter(x => x.price)
          .min(x => x.price as number);

        const formatted = sorted.map(x => {
          const clone = { ...x };
          if (cheapest) {
            clone.isCheapest = x.price === cheapest.price;
          }

          clone.price = x.price
            ? (x.price as number).formatCurrency({
                isCent: false,
                round: true,
              })
            : 'n/a';
          ('');
          return clone;
        });

        return formatted;
      }),
    );
  };

  export const getFlightsByGeo =
    (): Observable<SkyscannerResultsV3.NearestPlacesResponse> => {
      const body = {
        query: {
          locale: NetworkHelper.getLocale(),
          market: NetworkHelper.getCountry(),
          currency: NetworkHelper.getCurrency().currency,
        },
      };

      return ApiModule.get()
        .apiML.post<
          MlAbstractResponse<SkyscannerResultsV3.NearestPlacesResponse>
        >('/flights/locator', body)
        .pipe(map(it => it.data));
    };
}
